'use client';

import {
  AccountActiveIcon,
  AccountInactiveIcon,
  AgentBuilderActiveIcon,
  AgentsActiveIcon,
  AgentsBuilderInactiveIcon,
  AgentsInactiveIcon,
  AmplifyActiveIcon,
  AmplifyInactiveIcon,
  BillingActiveIcon,
  BillingInactiveIcon,
  ChatActiveIcon,
  ChatInactiveIcon,
  ContentGenerationActiveIcon,
  ContentGenerationInactiveIcon,
  ContextLibraryActiveIcon,
  ContextLibraryInactiveIcon,
  DashboardActiveIcon,
  DashboardInactiveIcon,
  GlossaryActiveIcon,
  GlossaryInactiveIcon,
  IntegrationsActiveIcon,
  IntegrationsInactiveIcon,
  OverviewActiveIcon,
  OverviewInactiveIcon,
  ProjectsActiveIcon,
  ProjectsInactiveIcon,
  SavedAgentsActiveIcon,
  SavedAgentsInactiveIcon,
  SettingsActiveIcon,
  SettingsInactiveIcon,
  SmartTasksActiveIcon,
  SmartTasksInactiveIcon,
  StyleGuideActiveIcon,
  StyleGuideInactiveIcon,
  TranslationMemoryActiveIcon,
  TranslationMemoryInactiveIcon,
  TranslationsActiveIcon,
  TranslationsInactiveIcon
} from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useFeatureFlags } from 'providers/ClientProviders/useFeatureFlags';
import { useMemo } from 'react';
import { IMenuItem } from './MenuItem';

interface IUseMenuOut {
  menuPartI: IMenuItem[];
  menuPartII: IMenuItem[];
}

export const factoryMenuActiveRule = (route: string, pathname: string) =>
  pathname?.search(route) !== -1;

const useMenu = (): IUseMenuOut => {
  const t = useTranslations();

  const { data: featureFlags } = useFeatureFlags();

  const menuPartI: Array<IMenuItem> = useMemo(
    () => [
      {
        iconActive: <DashboardActiveIcon className="h-6 w-6" />,
        iconInactive: <DashboardInactiveIcon className="h-6 w-6" />,
        name: t('components.menu.title.dashboard'),
        route: '/dashboard',
        visible: true
      },
      {
        iconActive: <ProjectsActiveIcon className="h-6 w-6" />,
        iconInactive: <ProjectsInactiveIcon className="h-6 w-6" />,
        name: t('components.menu.title.projects'),
        route: '/projects',
        visible: featureFlags?.projectsEnabled
      },
      {
        iconActive: <SmartTasksActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <SmartTasksInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.smart_tasks'),
        route: '/smart-tasks/chat-ai',
        visible: true,
        childrens: [
          {
            iconActive: <ChatActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <ChatInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.chat'),
            route: '/smart-tasks/chat-ai',
            visible: true
          },
          {
            iconActive: <ContextLibraryActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <ContextLibraryInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.context_library'),
            route: '/smart-tasks/context-library',
            visible: true
          }
        ]
      },
      {
        iconActive: <AgentsActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <AgentsInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.agents'),
        route: '/agents/agent-builder',
        visible: featureFlags?.agentsEnabled,
        notCollapsible: false,
        childrens: [
          {
            iconActive: <AgentBuilderActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <AgentsBuilderInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.agent_builder'),
            route: '/agents/agent-builder',
            visible: true
          },
          {
            iconActive: <SavedAgentsActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <SavedAgentsInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.saved_agents'),
            route: '/agents/saved-agents',
            visible: true
          }
        ]
      },
      {
        iconActive: <ContentGenerationActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <ContentGenerationInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.content_generation'),
        route: '/content-creator',
        visible: featureFlags?.contentGenerationEnabled
      },
      {
        iconActive: <AmplifyActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <AmplifyInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.amplify'),
        route: '/amplify',
        visible: featureFlags?.amplifyEnabled
      },
      {
        iconActive: <TranslationsActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <TranslationsInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.translation'),
        route: '/translation/translation-requests',
        visible: true,
        notCollapsible: true,
        childrens: [
          {
            iconActive: <OverviewActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <OverviewInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.overview'),
            route: '/translation/translation-requests',
            visible: true
          },
          {
            iconActive: <StyleGuideActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <StyleGuideInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.style_guides'),
            route: '/style-guides',
            visible: true
          },
          {
            iconActive: <GlossaryActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <GlossaryInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.glossary'),
            route: '/glossary',
            visible: true
          },
          {
            iconActive: <TranslationMemoryActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <TranslationMemoryInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.translation_memory'),
            route: '/translation-memory',
            visible: true
          }
        ]
      },
      {
        iconActive: <IntegrationsActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <IntegrationsInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.integrations'),
        route: '/integrations',
        visible: true
      }
    ],
    [
      ,
      featureFlags?.agentsEnabled,
      featureFlags?.amplifyEnabled,
      featureFlags?.contentGenerationEnabled,
      featureFlags?.projectsEnabled,
      t
    ]
  );

  const menuPartII: Array<IMenuItem> = useMemo(
    () => [
      {
        iconActive: <SettingsActiveIcon className="h-6 w-6 transition-all" />,
        iconInactive: <SettingsInactiveIcon className="h-6 w-6 transition-all" />,
        name: t('components.menu.title.settings'),
        route: '/account-details',
        visible: true,
        childrens: [
          {
            iconActive: <AccountActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <AccountInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.account_details'),
            route: '/account-details',
            visible: true
          },
          {
            iconActive: <BillingActiveIcon className="h-6 w-6 transition-all" />,
            iconInactive: <BillingInactiveIcon className="h-6 w-6 transition-all" />,
            name: t('components.menu.title.billing'),
            route: '/billing',
            visible: true
          }
        ]
      }
    ],
    [t]
  );

  return {
    menuPartI,
    menuPartII
  };
};

export default useMenu;
